import { useCallback } from 'react';
import { Shape, Tool } from '../types/Shape';
import { findNearestConnectionPoint, findShapeAtPoint, getRelativePosition } from '../utils/connectionUtils';

export interface UseCanvasMouseUpProps {
  // Current state
  shapes: Shape[];
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing: boolean;
  currentShape: Partial<Shape> | null;
  isResizing: boolean;
  isCanvasDragging: boolean;
  isWheelPanning: boolean;
  isConnecting: boolean;
  isLassoSelecting: boolean;
  lassoStart: { x: number; y: number } | null;
  lassoEnd: { x: number; y: number } | null;
  defaultStartArrow: boolean;
  defaultEndArrow: boolean;

  // Refs
  canvasDragRafRef: React.MutableRefObject<number | null>;
  hasActionStarted: React.MutableRefObject<boolean>;

  // Setters
  setIsWheelPanning: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCanvasDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setCanvasDragStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLassoSelecting: React.Dispatch<React.SetStateAction<boolean>>;
  setLassoStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setLassoEnd: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setIsResizing: React.Dispatch<React.SetStateAction<boolean>>;
  setResizeHandle: React.Dispatch<React.SetStateAction<string | null>>;
  setResizeStartPos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setResizeStartShape: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;
  setShowConnectionPoints: React.Dispatch<React.SetStateAction<boolean>>;
  setSnappedPoint: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setCurrentShape: React.Dispatch<React.SetStateAction<Partial<Shape> | null>>;
  setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setCurrentTool: React.Dispatch<React.SetStateAction<Tool>>;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseCanvasMouseUpReturn {
  handleStageMouseUp: (e: any) => void;
}

export function useCanvasMouseUp({
  shapes,
  stagePos,
  stageScale,
  isDrawing,
  currentShape,
  isResizing,
  isCanvasDragging,
  isWheelPanning,
  isConnecting,
  isLassoSelecting,
  lassoStart,
  lassoEnd,
  defaultStartArrow,
  defaultEndArrow,
  canvasDragRafRef,
  hasActionStarted,
  setIsWheelPanning,
  setIsCanvasDragging,
  setCanvasDragStart,
  setSelectedShapes,
  setShowResizeHandles,
  setIsLassoSelecting,
  setLassoStart,
  setLassoEnd,
  setIsResizing,
  setResizeHandle,
  setResizeStartPos,
  setResizeStartShape,
  setShowConnectionPoints,
  setSnappedPoint,
  setCurrentShape,
  setIsConnecting,
  setShapes,
  setCurrentTool,
  setIsDrawing,
  setHasUnsavedChanges,
}: UseCanvasMouseUpProps): UseCanvasMouseUpReturn {

  const handleStageMouseUp = useCallback((e: any) => {
    // Wheel button release
    if (e.evt.button === 1) {
      if (canvasDragRafRef.current !== null) {
        cancelAnimationFrame(canvasDragRafRef.current);
      }
      setIsWheelPanning(false);
      setIsCanvasDragging(false);
      setCanvasDragStart(null);
      const stage = e.target.getStage();
      if (stage && stage.container()) {
        stage.container().style.cursor = 'default';
      }
      return;
    }

    // Ignore non-left button
    if (e.evt.button !== 0) {
      return;
    }

    // End canvas drag (non-wheel button)
    if (isCanvasDragging && !isWheelPanning) {
      setIsCanvasDragging(false);
      setCanvasDragStart(null);
      return;
    }

    // End lasso selection - select shapes in area
    if (isLassoSelecting && lassoStart && lassoEnd) {
      const minX = Math.min(lassoStart.x, lassoEnd.x);
      const maxX = Math.max(lassoStart.x, lassoEnd.x);
      const minY = Math.min(lassoStart.y, lassoEnd.y);
      const maxY = Math.max(lassoStart.y, lassoEnd.y);

      // AABB intersection test
      const selectedIds = shapes
        .filter(shape => {
          let shapeMinX, shapeMaxX, shapeMinY, shapeMaxY;

          if (shape.type === 'arrow') {
            const width = shape.width || 0;
            const height = shape.height || 0;
            shapeMinX = Math.min(shape.x, shape.x + width);
            shapeMaxX = Math.max(shape.x, shape.x + width);
            shapeMinY = Math.min(shape.y, shape.y + height);
            shapeMaxY = Math.max(shape.y, shape.y + height);
          } else {
            const width = shape.width || 0;
            const height = shape.height || 0;
            shapeMinX = shape.x;
            shapeMaxX = shape.x + width;
            shapeMinY = shape.y;
            shapeMaxY = shape.y + height;
          }

          return !(shapeMaxX < minX || shapeMinX > maxX ||
            shapeMaxY < minY || shapeMinY > maxY);
        })
        .map(shape => shape.id);

      setSelectedShapes(selectedIds);
      if (selectedIds.length > 0) {
        setShowResizeHandles(true);
      }

      setIsLassoSelecting(false);
      setLassoStart(null);
      setLassoEnd(null);
      return;
    }

    // End resize
    if (isResizing) {
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStartPos(null);
      setResizeStartShape(null);
      hasActionStarted.current = false;
      setShowConnectionPoints(false);
      setSnappedPoint(null);
      return;
    }

    // Complete arrow drawing
    if (isConnecting && currentShape && currentShape.type === 'arrow') {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const actualX = (pointer.x - stagePos.x) / stageScale;
          const actualY = (pointer.y - stagePos.y) / stageScale;

          const nearestPoint = findNearestConnectionPoint(actualX, actualY, shapes, 20 / stageScale);

          let endX = actualX;
          let endY = actualY;
          let endShapeId: string | undefined;
          let endConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
          let endRelativeX: number | undefined;
          let endRelativeY: number | undefined;

          if (nearestPoint) {
            endX = nearestPoint.x;
            endY = nearestPoint.y;
            endShapeId = nearestPoint.shapeId;
            endConnectionPoint = nearestPoint.pointType;
          } else {
            const shapeAtPoint = findShapeAtPoint(actualX, actualY, shapes);
            if (shapeAtPoint) {
              endShapeId = shapeAtPoint.id;
              const relativePos = getRelativePosition(actualX, actualY, shapeAtPoint);
              if (relativePos) {
                endRelativeX = relativePos.relativeX;
                endRelativeY = relativePos.relativeY;
              }
            }
          }

          const startX = (currentShape as Shape).x || 0;
          const startY = (currentShape as Shape).y || 0;
          const arrowWidth = endX - startX;
          const arrowHeight = endY - startY;

          // Minimum distance check (5px)
          const distance = Math.sqrt(arrowWidth * arrowWidth + arrowHeight * arrowHeight);
          if (distance < 5) {
            setCurrentShape(null);
            setIsConnecting(false);
            setShowConnectionPoints(false);
            setSnappedPoint(null);
            return;
          }

          // 곡선 화살표인 경우 자동으로 중간 웨이포인트 추가 (곡선 위에 빨간점)
          const isCurved = (currentShape as Shape).isCurved;
          let waypoints: { x: number; y: number }[] | undefined;

          if (isCurved) {
            // 시작점과 끝점의 중간에 웨이포인트 추가
            const midX = startX + arrowWidth / 2;
            const midY = startY + arrowHeight / 2;

            // 수직 방향으로 약간 오프셋 (곡선 효과)
            const dx = arrowWidth;
            const dy = arrowHeight;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const offsetRatio = 0.2; // 선 길이의 20%만큼 오프셋

            if (distance > 0) {
              // 수직 방향 계산
              const perpX = -dy / distance;
              const perpY = dx / distance;
              const offset = distance * offsetRatio;

              waypoints = [{
                x: midX + perpX * offset,
                y: midY + perpY * offset
              }];
            } else {
              waypoints = [{ x: midX, y: midY }];
            }
          }

          const completedArrow: Shape = {
            ...currentShape as Shape,
            id: Date.now().toString(),
            x: startX,
            y: startY,
            width: arrowWidth,
            height: arrowHeight,
            endShapeId,
            endConnectionPoint,
            endRelativeX,
            endRelativeY,
            startArrow: (currentShape as Shape).startArrow ?? defaultStartArrow,
            endArrow: (currentShape as Shape).endArrow ?? defaultEndArrow,
            waypoints,
          };

          setShapes(prev => {
            const newShapes = [...prev, completedArrow];

            // Update connected arrows info on start/end shapes
            if (completedArrow.startShapeId) {
              newShapes.forEach(shape => {
                if (shape.id === completedArrow.startShapeId) {
                  if (!shape.connectedArrows) {
                    shape.connectedArrows = [];
                  }
                  shape.connectedArrows.push(completedArrow.id);
                }
              });
            }

            if (completedArrow.endShapeId) {
              newShapes.forEach(shape => {
                if (shape.id === completedArrow.endShapeId) {
                  if (!shape.connectedArrows) {
                    shape.connectedArrows = [];
                  }
                  shape.connectedArrows.push(completedArrow.id);
                }
              });
            }

            return newShapes;
          });

          setCurrentShape(null);
          setIsConnecting(false);
          setShowConnectionPoints(false);
          setSnappedPoint(null);
          setCurrentTool('select');
          setHasUnsavedChanges(true);
        }
      }
      return;
    }

    // Complete shape drawing
    if (!isDrawing || !currentShape) return;

    const shapeWidth = Math.abs((currentShape as Shape).width || 0);
    const shapeHeight = Math.abs((currentShape as Shape).height || 0);

    // Minimum size check (5px)
    if (shapeWidth < 5 || shapeHeight < 5) {
      setCurrentShape(null);
      setIsDrawing(false);
      return;
    }

    setShapes(prev => [...prev, currentShape as Shape]);
    setCurrentShape(null);
    setIsDrawing(false);
    setCurrentTool('select');
    hasActionStarted.current = false;
    setHasUnsavedChanges(true);
  }, [
    shapes,
    stagePos,
    stageScale,
    isDrawing,
    currentShape,
    isResizing,
    isCanvasDragging,
    isWheelPanning,
    isConnecting,
    isLassoSelecting,
    lassoStart,
    lassoEnd,
    defaultStartArrow,
    defaultEndArrow,
    canvasDragRafRef,
    hasActionStarted,
    setIsWheelPanning,
    setIsCanvasDragging,
    setCanvasDragStart,
    setSelectedShapes,
    setShowResizeHandles,
    setIsLassoSelecting,
    setLassoStart,
    setLassoEnd,
    setIsResizing,
    setResizeHandle,
    setResizeStartPos,
    setResizeStartShape,
    setShowConnectionPoints,
    setSnappedPoint,
    setCurrentShape,
    setIsConnecting,
    setShapes,
    setCurrentTool,
    setIsDrawing,
    setHasUnsavedChanges,
  ]);

  return { handleStageMouseUp };
}
